// Web receipt renderer — the in-browser equivalent of Android getHTMLEmailReceipt/printPaymentReceipt.
// Builds a structured model from the finalized cart state, renders it to an ~80mm HTML receipt, and
// prints it via the browser (window.print in a hidden iframe). The SAME HTML is sent to the
// email/SMS endpoint. Reprint-history, the template editor, and true thermal ESC/POS (needs a native
// bridge) are separate phases — this covers the everyday print/email/SMS flow.

import { auth } from "./api";
import { shouldHideNcaOnSummary } from "../engine/orderDetail";
import { dualTenderKind } from "../engine/dualPayload";
import { roundHalfUp } from "../engine/money";

// Negative amounts (lottery/scratcher payout lines, a payout-only cash tender) print as "-$5.00",
// matching the cart's display — "$-5.00" was what the receipt preview showed for a payout order.
const money = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return (v < 0 ? "-" : "") + "$" + Math.abs(v).toFixed(2);
};
const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Build a receipt model from the finalized cart slice state + the order id.
export function buildReceiptModel({ cart, orderId, cashPaid = 0, store = {}, isSurcharge, payload } = {}) {
  const a = auth();
  const totals = cart.totals || {};
  const lines = (cart.lines || []).map((l) => ({
    qty: Number(l.orderCount) || 0,
    name: l.orderName,
    price: Number(l.jsonNewPrice) || 0, // per-line NET — already includes manual discount + item coupon
    promo: l.isBoGoProduct ? l.isWarningShow || "BOGO" : "",
    // QV-4738 item coupon: shown as its own "Coupon(CODE)" line under the item (Android
    // InStoreOrderItemAdapterNew) — informational; the price above is already net of it.
    coupon: l.couponDiscountName
      ? { code: String(l.couponDiscountName), amount: (Number(l.couponDiscountFinalAmt) || 0) * (Number(l.orderCount) || 0) }
      : null,
    // QV-7124: a manufacturer-funded discount is ATTRIBUTED on the receipt ("You saved $X
    // (Manufacturer Discount)") rather than shown as a plain saving — Altria requires their funding
    // to be named. LINE total = per-unit × qty.
    saved: (Number(l.deziItemDiscountAmt) || 0) * (Number(l.orderCount) || 0),
    savedLabel: (Number(l.deziItemDiscountAmt) || 0) > 0 ? "Manufacturer Discount" : "",
  }));
  const payments = (cart.payments || []).map((p) => ({
    label:
      String(p.payType).toLowerCase() === "cash"
        ? "Cash"
        : p.cardType || String(p.payType).replace(/^\w/, (c) => c.toUpperCase()),
    amount: Number(p.amount) || 0,
    nca: Number(p.ncaAmount) || 0,
    cashback: Number(p.cashbackAmount) || 0,
    cashbackFee: Number(p.cashbackFee) || 0,
    last4: p.last4 || "",
  }));
  const nca = payments.reduce((s, p) => s + p.nca, 0);
  const cashbackAmt = payments.reduce((s, p) => s + p.cashback, 0);
  const cashbackFee = payments.reduce((s, p) => s + p.cashbackFee, 0);
  const tenderTotal = payments.reduce((s, p) => s + p.amount, 0);
  const cashOnly = payments.length === 1 && String((cart.payments[0] || {}).payType).toLowerCase() === "cash";
  const lastPay = (cart.payments || [])[(cart.payments || []).length - 1];
  const lastIsCash = !!lastPay && String(lastPay.payType).toLowerCase() === "cash";
  const changeDue = cashOnly
    ? Math.max(Math.round((Number(cashPaid) - totals.grandTotal) * 100) / 100, 0)
    : lastIsCash && Number(cashPaid) > 0
    ? Math.max(Math.round((Number(cashPaid) - (Number(lastPay.amount) || 0)) * 100) / 100, 0)
    : 0;
  const giftCards = (cart.giftCards || []).map((g) => ({ number: g.number, amount: Number(g.amountUsed) || 0 }));
  const model = {
    orderId,
    // Android ReceiptFragment: an order with a lottery/scratcher redemption can't be printed/sent.
    hasLotteryPayout: (cart.lines || []).some((l) => l.isLotteryPayout || l.isScratcherPayout),
    dateTime: new Date().toLocaleString(),
    cashier: a.employeeName || "",
    store: {
      name: store.name || a.storeName || "",
      address: store.address || a.storeAddress || "",
      phone: store.phone || a.storePhone || "",
    },
    itemCount: lines.reduce((s, l) => s + l.qty, 0),
    lines,
    subtotal: Number(totals.subtotal) || 0,
    discount: (Number(cart.couponTotal) || 0) + (Number(cart.customTotal) || 0),
    taxLines: (totals.taxLines || []).map((t) => ({ name: t.name, percent: Number(t.percent) || 0, percentRaw: t.percentRaw || "", amount: Number(t.amount) || 0 })),
    tax: Number(totals.taxRateInAmount) || 0,
    nca,
    hideNca: shouldHideNcaOnSummary(isSurcharge),
    tip: payments.reduce((s, p) => s + (Number(p.tip) || 0), 0),
    grandTotal: Number(totals.grandTotal) || 0,
    payments,
    cashbackAmt,
    cashbackFee,
    changeDue,
    cashPaid: cashOnly ? Number(cashPaid) || 0 : 0,
    giftCards,
    loyaltySpent: Number(cart.loyaltyAmount) || 0,
    storeCredit: Number(cart.storeCreditAmount) || 0,
    tenderTotal,
  };
  return applyDualReceipt(model, { cart, payload, cashPaid });
}

/**
 * QV-4241 dual price — Android receipts render the SAVED order: a cash/debit/EBT sale prints the CASH
 * columns, a credit sale the CARD columns; card rows show what the card was charged (base + NCA) and
 * no separate NCA row is printed. Off dual price the model is returned untouched.
 */
export function applyDualReceipt(model, { cart, payload, cashPaid = 0 }) {
  const d = cart && cart.totals && cart.totals.dual;
  if (!d || !d.dual) return model;
  const kind = dualTenderKind(cart.payments || []);
  const cols =
    kind === "credit"
      ? { sub: d.cardSubTotal, disc: d.cardOrderDiscount, tax: d.cardTax, taxLines: d.cardTaxLines, total: d.cardGrandTotal }
      : kind === "cash"
      ? { sub: d.baseSubTotal, disc: d.baseOrderDiscount, tax: d.baseTax, taxLines: d.baseTaxLines, total: d.baseGrandTotal }
      : null;
  const out = { ...model, hideNca: true, payments: model.payments.map((p) => ({ ...p, amount: roundHalfUp(p.amount + p.nca), nca: 0 })), nca: 0 };
  let items = [];
  try {
    items = payload && payload.item_ses ? JSON.parse(payload.item_ses) : [];
  } catch {
    items = [];
  }
  if (items.length === out.lines.length) {
    out.lines = out.lines.map((l, i) => {
      const it = items[i] || {};
      const unit = Math.max(Number(it.price || 0) - Number(it.discount_amt || 0) - Number(it.item_coupon_discount_amount || 0), 0);
      return { ...l, price: roundHalfUp(unit * (l.qty || 1)) };
    });
  }
  if (cols) {
    const pct = new Map((model.taxLines || []).map((t) => [t.name, t]));
    out.subtotal = cols.sub;
    out.discount = cols.disc;
    out.tax = cols.tax;
    out.taxLines = (cols.taxLines || []).map((t) => ({ ...(pct.get(t.name) || {}), name: t.name, amount: t.amount }));
    out.grandTotal = cols.total;
  } else {
    out.grandTotal = roundHalfUp(Number(payload && payload.amt) || out.tenderTotal);
  }
  out.tenderTotal = roundHalfUp(out.payments.reduce((s, p) => s + p.amount, 0));
  if (out.changeDue > 0 || out.cashPaid > 0) out.changeDue = Math.max(roundHalfUp(Number(cashPaid) - d.baseGrandTotal), 0);
  return out;
}

// Render the model to a printable HTML string (~80mm thermal width).
export function renderReceiptHTML(m) {
  const row = (l, r, opt = {}) =>
    `<div style="display:flex;justify-content:space-between;${opt.bold ? "font-weight:500;" : ""}${opt.big ? "font-size:15px;" : ""}"><span>${l}</span><span>${r}</span></div>`;
  const div = '<div style="border-top:1px dashed #000;margin:6px 0;"></div>';
  const items = m.lines
    .map(
      (l) =>
        `<div style="display:flex;justify-content:space-between;"><span>${l.qty} x ${esc(l.name)}</span><span>${money(l.price)}</span></div>` +
        (l.promo ? `<div style="font-size:11px;color:#444;">${esc(l.promo)}</div>` : "") +
        (l.coupon ? `<div style="display:flex;justify-content:space-between;font-size:11px;color:#444;"><span>Coupon(${esc(l.coupon.code)})</span><span>(-${money(l.coupon.amount)})</span></div>` : "") +
        // QV-7124 — the manufacturer's funding is NAMED, not shown as an anonymous saving.
        (l.saved > 0 ? `<div style="font-size:11px;color:#444;">You saved ${money(l.saved)}!${l.savedLabel ? ` (${esc(l.savedLabel)})` : ""}</div>` : "")
    )
    .join("");
  const taxRows = (m.taxLines.length
    ? m.taxLines.map((t) => row(`${esc(t.name)}${t.percent ? ` (${esc(t.percentRaw || String(t.percent))}%)` : ""}`, money(t.amount)))
    : [m.tax > 0 ? row("Tax", money(m.tax)) : ""]
  ).join("");
  const tenders = m.payments.map((p) => row(`${esc(p.label)}${p.last4 ? `-${p.last4}` : ""}`, money(p.amount))).join("");
  const gc = m.giftCards.map((g) => row(`Gift Card (${esc(g.number)})`, `-${money(g.amount)}`)).join("");
  return `<div style="font-family:'Courier New',monospace;font-size:12px;width:280px;margin:0 auto;color:#000;">
    ${m.store.name ? `<div style="text-align:center;font-weight:500;font-size:15px;">${esc(m.store.name)}</div>` : ""}
    ${m.store.address ? `<div style="text-align:center;">${esc(m.store.address)}</div>` : ""}
    ${m.store.phone ? `<div style="text-align:center;">${esc(m.store.phone)}</div>` : ""}
    <div style="text-align:center;margin-top:4px;">Order ${esc(m.orderId)}</div>
    <div style="text-align:center;">${esc(m.dateTime)}</div>
    ${m.cashier ? `<div style="text-align:center;">Cashier: ${esc(m.cashier)}</div>` : ""}
    <div style="text-align:center;"># of Items: ${m.itemCount}</div>
    ${div}${items}${div}
    ${row("Sub Total", money(m.subtotal))}
    ${m.discount > 0 ? row("Discount", `-${money(m.discount)}`) : ""}
    ${taxRows}
    ${m.nca > 0 && !m.hideNca ? row("Non-Cash Adjustment", money(m.nca)) : ""}
    ${m.tip > 0 ? row("Tip", money(m.tip)) : ""}
    ${div}${row("GRAND TOTAL", money(m.grandTotal), { bold: true, big: true })}${div}
    ${gc}
    ${m.loyaltySpent > 0 ? row("Loyalty Applied", `-${money(m.loyaltySpent)}`) : ""}
    ${m.storeCredit > 0 ? row("Store Credit Used", `-${money(m.storeCredit)}`) : ""}
    ${tenders}
    ${m.cashbackAmt > 0 ? row("Cash Back", money(m.cashbackAmt)) : ""}
    ${m.cashbackFee > 0 ? row("Cash Back Fee", money(m.cashbackFee)) : ""}
    ${m.cashPaid > 0 ? row("Cash Paid", money(m.cashPaid)) : ""}
    ${m.changeDue > 0 ? row("Change Due", money(m.changeDue), { bold: true }) : ""}
    <div style="text-align:center;margin-top:10px;">Thank you!</div>
  </div>`;
}

// Refund receipt (Android printRefundReceiptItem, simplified) — same ~80mm layout as the sale
// receipt. `m`: { store:{name,address,phone}, orderId, dateTime, cashier, items:[{qty,name,amount}],
// tipAmount, ncaAmount, total, method ("Cash"|"Credit"|"Debit"), reason }.
export function renderRefundReceiptHTML(m) {
  const row = (l, r, opt = {}) =>
    `<div style="display:flex;justify-content:space-between;${opt.bold ? "font-weight:500;" : ""}${opt.big ? "font-size:15px;" : ""}"><span>${l}</span><span>${r}</span></div>`;
  const div = '<div style="border-top:1px dashed #000;margin:6px 0;"></div>';
  const items = (m.items || [])
    .map((l) => `<div style="display:flex;justify-content:space-between;"><span>${l.qty} x ${esc(l.name)}</span><span>-${money(l.amount)}</span></div>`)
    .join("");
  return `<div style="font-family:'Courier New',monospace;font-size:12px;width:280px;margin:0 auto;color:#000;">
    ${m.store && m.store.name ? `<div style="text-align:center;font-weight:500;font-size:15px;">${esc(m.store.name)}</div>` : ""}
    ${m.store && m.store.address ? `<div style="text-align:center;">${esc(m.store.address)}</div>` : ""}
    ${m.store && m.store.phone ? `<div style="text-align:center;">${esc(m.store.phone)}</div>` : ""}
    <div style="text-align:center;font-weight:500;font-size:14px;margin-top:6px;">*** REFUND ***</div>
    <div style="text-align:center;">Order ${esc(m.orderId)}</div>
    <div style="text-align:center;">${esc(m.dateTime)}</div>
    ${m.cashier ? `<div style="text-align:center;">Cashier: ${esc(m.cashier)}</div>` : ""}
    ${div}${items}${div}
    ${Number(m.tipAmount) > 0 ? row("Tip Refund", `-${money(m.tipAmount)}`) : ""}
    ${Number(m.ncaAmount) > 0 ? row("Non-Cash Adj. Refund", `-${money(m.ncaAmount)}`) : ""}
    ${row("TOTAL REFUNDED", `-${money(m.total)}`, { bold: true, big: true })}
    ${row("Refunded to", esc(m.method || "Cash"))}
    ${m.reason ? `<div style="margin-top:4px;">Reason: ${esc(m.reason)}</div>` : ""}
    <div style="text-align:center;margin-top:10px;">Thank you!</div>
  </div>`;
}

// Print via a hidden iframe (browser print dialog → any OS/driver printer, incl. a thermal one
// installed as a system printer).
export function printReceiptHTML(html) {
  try {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<html><head><title>Receipt</title></head><body onload="window.print()">${html}</body></html>`);
    doc.close();
    setTimeout(() => document.body.removeChild(iframe), 60000);
  } catch {
    /* pop-up/iframe blocked — caller may fall back to a new tab */
  }
}
